import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/contexts/useAuth";
import { apiDelete, apiGet, apiPost, getApiErrorCode, getApiErrorMessage, type ApiResult } from "@/api";
import { toDisplayString } from "@/lib/utils";
import { tokenQuery } from "./query-options";
import type { QueryKey } from "@tanstack/react-query";

export interface TeamMember {
  id: string;
  teamId: string;
  userId: string;
  role: string;
  joinedAt: string;
  email?: string;
}

export interface TeamInvite {
  id: string;
  teamId: string;
  email: string;
  invitedBy: string;
  accepted: boolean;
  createdAt: string;
}

export interface TeamData {
  id: string;
  name: string;
  ownerId: string;
  createdAt: string;
  currentUserRole: string | null;
  members: TeamMember[];
  invites: TeamInvite[];
}

export interface TeamQueryData {
  team: TeamData | null;
  teamRequired: boolean;
}

/**
 * Turns a `/team` response into either the team or the upgrade offer, or throws.
 *
 * Same shape and same reasoning as selectLeadSignals, one gate over: `403 team_required` is the
 * product telling a non-Team user what the Team plan would give them. Reported as
 * `teamRequired: false`, `team: null` reads as "you have no team yet" — the same page a Team
 * customer who has not created one sees — and the upgrade offer and its `paywall_shown` event
 * disappear together.
 */
export function selectTeam(res: ApiResult<{ team: TeamData | null }>): TeamQueryData {
  if (!res.ok) {
    if (res.status === 403 && getApiErrorCode(res) === "team_required") {
      return { team: null, teamRequired: true };
    }
    throw new Error(getApiErrorMessage(res, "Team konnte nicht geladen werden"));
  }
  return { team: res.data.team, teamRequired: false };
}

export function teamQueryOptions(token: string | null) {
  return tokenQuery(["team"], token, async (t) =>
    selectTeam(await apiGet<{ team: TeamData | null }>("/team", t)),
  );
}

export function useTeam() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(teamQueryOptions(token));
}

/** Creating a team, inviting a member and removing one all change the same `/team` answer. */
export function teamInvalidateKeys(token: string | null): readonly QueryKey[] {
  return [teamQueryOptions(token).queryKey];
}

export function useCreateTeam() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (name: string) => {
      if (!token) throw new Error("Nicht angemeldet");
      const res = await apiPost("/team", token, { name });
      if (!res.ok) throw new Error(getApiErrorMessage(res, "Team konnte nicht erstellt werden"));
      return res.data;
    },
    onSuccess: () => {
      for (const queryKey of teamInvalidateKeys(token)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

export function useInviteTeamMember() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (email: string) => {
      if (!token) throw new Error("Nicht angemeldet");
      const res = await apiPost("/team/invite", token, { email });
      if (!res.ok) throw new Error(getApiErrorMessage(res, "Einladung konnte nicht gesendet werden"));
      return res.data;
    },
    onSuccess: () => {
      for (const queryKey of teamInvalidateKeys(token)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

export interface PendingInvite {
  id: string;
  teamId: string;
  teamName: string;
  invitedBy: string;
  createdAt: string;
}

/**
 * Turns a `/team/invites/me` response into the pending invitations, or throws.
 *
 * It used to be `if (!res.ok) return []`, and it was the only hook in this app that swallowed a
 * failure unconditionally — every sibling either throws or returns a documented paywall flag and
 * then throws. The consequence is the worst one this class produces: a user who HAS been invited
 * to a team is told they have none, on the only page that offers the accept button, so the
 * invitation exists and is unreachable. `resolveViewState` cannot catch it either — it reads the
 * query's `error`, and a fabricated empty list has none, so the page answers `ready`.
 *
 * An empty list means the server said the user has no invitations. Nothing else may produce one.
 */
export function selectMyTeamInvites(res: ApiResult<{ invites?: PendingInvite[] }>): PendingInvite[] {
  if (!res.ok) throw new Error(getApiErrorMessage(res, "Einladungen konnten nicht geladen werden"));
  return res.data.invites ?? [];
}

/** What the team page shows where the pending invitations go. */
export type InvitesPanel =
  | { kind: "hidden" }
  | { kind: "unavailable" }
  | { kind: "list"; invites: readonly PendingInvite[] };

/**
 * Decides whether an invitation is shown, moved out of JSX because that is where it was wrong.
 *
 * Two separate paths were hiding a real invitation, and the accept button lives inside this
 * panel — so in both the invitation existed and the user had no way to take it:
 *
 *   1. A failed request became an empty list (see selectMyTeamInvites above).
 *   2. `teamRequired` hid the panel. That is the INVITEE's normal state: `GET /team` answers
 *      `403 team_required` to anyone not already on a team plan, which every invited user is by
 *      definition. The API is deliberately built for them — `GET /team/invites/me` carries no
 *      team gate, and `POST /team/invite/:id/accept` says so in a comment: "No team-plan check —
 *      the invitee may still be on the free plan before joining." The dashboard showed those
 *      users the upgrade offer instead of the seat someone had already bought them. So
 *      `teamRequired` is deliberately absent from this function's input, and its absence is the
 *      fix.
 *
 * Deliberately NOT decided here: the team query's own error does not hide the panel. An
 * invitation is independent data that arrived successfully, and the page already reports the
 * team failure separately. Preserved from the previous behaviour: a user who is already in a
 * team is not shown invitations to another one.
 */
export function invitesPanel(input: {
  readonly invites: readonly PendingInvite[] | undefined;
  readonly invitesError: unknown;
  readonly team: TeamData | null;
  readonly teamLoading: boolean;
}): InvitesPanel {
  // A failure outranks a pending load, the same rule lib/query-state.ts applies: once the call
  // has failed, waiting only delays telling the user that we do not know.
  if (input.invitesError != null) return { kind: "unavailable" };
  if (input.teamLoading) return { kind: "hidden" };
  if (input.team) return { kind: "hidden" };
  if (!input.invites || input.invites.length === 0) return { kind: "hidden" };
  return { kind: "list", invites: input.invites };
}

export function myTeamInvitesQueryOptions(token: string | null) {
  return tokenQuery(["team-invites-me"], token, async (t) =>
    selectMyTeamInvites(await apiGet<{ invites?: PendingInvite[] }>("/team/invites/me", t)),
  );
}

export function useMyTeamInvites() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  return useQuery(myTeamInvitesQueryOptions(token));
}

/**
 * Accepting is the one mutation that changes BOTH: the user now has a team, and the invitation
 * they just took is gone. Clearing only `team` would leave the accepted invitation on screen
 * with its accept button still offered — the panel is driven by the invites query, not by the
 * team query, so the stale half is the visible half.
 */
export function acceptTeamInviteInvalidateKeys(token: string | null): readonly QueryKey[] {
  return [teamQueryOptions(token).queryKey, myTeamInvitesQueryOptions(token).queryKey];
}

export function useAcceptTeamInvite() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (inviteId: string) => {
      if (!token) throw new Error("Nicht angemeldet");
      const res = await apiPost(`/team/invite/${encodeURIComponent(toDisplayString(inviteId))}/accept`, token, {});
      if (!res.ok) throw new Error(getApiErrorMessage(res, "Einladung konnte nicht angenommen werden"));
      return res.data;
    },
    onSuccess: () => {
      for (const queryKey of acceptTeamInviteInvalidateKeys(token)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}

export function useRemoveTeamMember() {
  const { state } = useAuth();
  const token = state.status === "authenticated" ? state.token : null;
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (userId: string) => {
      if (!token) throw new Error("Nicht angemeldet");
      const res = await apiDelete(`/team/members/${encodeURIComponent(toDisplayString(userId))}`, token);
      if (!res.ok) throw new Error(getApiErrorMessage(res, "Mitglied konnte nicht entfernt werden"));
      return res;
    },
    onSuccess: () => {
      for (const queryKey of teamInvalidateKeys(token)) {
        void queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}
